import type {TaskAndReminderAction} from 'src/action-creators/task-and-reminder';
import type {TaskAndReminderState} from 'src/types/task-and-reminder';

import {
  GET_TASK_DETAILS_ERROR,
  GET_TASK_DETAILS_LOADING,
  GET_TASK_DETAILS,
  FETCH_TASKS_LOADING,
  FETCH_TASKS_SUCCESS,
  FETCH_TASK_TYPES,
  RESET_TASK_DETAILS_ACTION,
  UPSERT_TASK_IN_TASK_LIST,
  REMOVE_TASK_FROM_TASK_LIST,
  SET_DASHBOARD_TASK_COUNT,
  SET_DASHBOARD_ACTIVE_TAB,
  PREPEND_TASK_TO_DASHBOARD_LIST,
} from 'src/action-creators/task-and-reminder';
import {DASHBOARD_TASKS_PREVIEW_LIMIT} from 'src/utils/task-and-reminder';


const initialState: TaskAndReminderState = {
  isGettingTaskDetails: false,
  getTaskDetailsError: null,
  taskDetails: null,
  isFetchingTasks: false,
  isFetchingMoreTasks: false,
  taskList: [],
  taskListTotalRecords: 0,
  taskTypes: [],
  dashboardTaskCounts: {overdue: 0, upcoming: 0},
  dashboardActiveTab: 'overdue',
};

export default (
  state: TaskAndReminderState = initialState,
  action: TaskAndReminderAction,
): TaskAndReminderState => {
  switch (action.type) {
    case GET_TASK_DETAILS_LOADING:
      return {
        ...state,
        isGettingTaskDetails: true,
        getTaskDetailsError: null,
      };
    case GET_TASK_DETAILS:
      return {
        ...state,
        isGettingTaskDetails: false,
        getTaskDetailsError: null,
        taskDetails: action.payload,
      };
    case RESET_TASK_DETAILS_ACTION:
      return {
        ...state,
        taskDetails: null,
      };
    case GET_TASK_DETAILS_ERROR:
      return {
        ...state,
        isGettingTaskDetails: false,
        getTaskDetailsError: action.payload,
      };
    case FETCH_TASKS_LOADING:
      return action.payload.append
        ? {
            ...state,
            isFetchingMoreTasks: true,
          }
        : {
            ...state,
            isFetchingTasks: true,
            isFetchingMoreTasks: false,
          };
    case FETCH_TASK_TYPES:
      return {
        ...state,
        taskTypes: action.payload,
      };
    case FETCH_TASKS_SUCCESS: {
      const append = action.meta.append;
      if (append) {
        if (action.payload.data.length === 0) {
          return {
            ...state,
            isFetchingMoreTasks: false,
            taskListTotalRecords: state.taskList.length,
          };
        }
        const merged = [...state.taskList, ...action.payload.data];
        return {
          ...state,
          isFetchingMoreTasks: false,
          taskList: merged,
          taskListTotalRecords: action.payload.total_records,
        };
      }
      return {
        ...state,
        isFetchingTasks: false,
        isFetchingMoreTasks: false,
        taskList: action.payload.data,
        taskListTotalRecords: action.payload.total_records,
      };
    }
    case UPSERT_TASK_IN_TASK_LIST: {
      const nextTask = action.payload;
      if (nextTask.id == null) {
        return state;
      }
      const idKey = String(nextTask.id);
      const hasMatch = state.taskList.some((task) => String(task.id) === idKey);
      if (!hasMatch) {
        return state;
      }
      return {
        ...state,
        taskList: state.taskList.map((task) =>
          String(task.id) === idKey ? nextTask : task,
        ),
      };
    }
    case REMOVE_TASK_FROM_TASK_LIST: {
      const {taskId} = action.payload;
      const idKey = String(taskId);
      const nextList = state.taskList.filter(
        (task) => String(task.id) !== idKey,
      );
      if (nextList.length === state.taskList.length) {
        return state;
      }
      return {
        ...state,
        taskList: nextList,
        taskListTotalRecords: Math.max(0, state.taskListTotalRecords - 1),
      };
    }
    case SET_DASHBOARD_TASK_COUNT: {
      const {tab, count} = action.payload;
      return {
        ...state,
        dashboardTaskCounts: {
          ...state.dashboardTaskCounts,
          [tab]: count,
        },
      };
    }
    case SET_DASHBOARD_ACTIVE_TAB:
      return {
        ...state,
        dashboardActiveTab: action.payload,
      };
    case PREPEND_TASK_TO_DASHBOARD_LIST: {
      const {task} = action.payload;
      const isUpcomingActive = state.dashboardActiveTab === 'upcoming';
      const canPrepend =
        isUpcomingActive &&
        state.taskList.length < DASHBOARD_TASKS_PREVIEW_LIMIT;
      return {
        ...state,
        taskList: canPrepend ? [task, ...state.taskList] : state.taskList,
        taskListTotalRecords: isUpcomingActive
          ? state.taskListTotalRecords + 1
          : state.taskListTotalRecords,
        dashboardTaskCounts: {
          ...state.dashboardTaskCounts,
          upcoming: state.dashboardTaskCounts.upcoming + 1,
        },
      };
    }
    default:
      return state;
  }
};
